import Select, { ClassNamesConfig, components, GroupBase, Props, SelectComponentsConfig } from 'react-select';
import Async, { AsyncProps } from 'react-select/async';
import AsyncCreatable, { AsyncCreatableProps } from 'react-select/async-creatable';
import Creatable, { CreatableProps } from 'react-select/creatable';

import { cn } from '@/lib/utils';

import ArrowDownSmallIcon from '../icons/arrow-down-small';
import { CloseSmallIcon } from '../icons/close-small';
import { BadgeVariant, badgeVariantClasses } from './badge';

type MultiSelectStyleProps = { error?: boolean; chipVariant?: BadgeVariant };

export function MultiSelect<Option>({ styles, components, chipVariant, ...props }: Props<Option, true> & MultiSelectStyleProps) {
  return (
    <Select
      {...props}
      classNames={MULTI_SELECT_STYLES<Option>(props.error, chipVariant)}
      components={{
        ...MULTI_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      // 'absolute' portal positioning grows body scroll height when adding tags pushes
      // the open menu past the viewport, creating a runaway phantom scrollbar
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      isMulti={true}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export function CreatableMultiSelect<Option>({
  styles,
  components,
  chipVariant,
  ...props
}: CreatableProps<Option, true, GroupBase<Option>> & MultiSelectStyleProps) {
  return (
    <Creatable
      {...props}
      classNames={MULTI_SELECT_STYLES<Option>(props.error, chipVariant)}
      components={{
        ...MULTI_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      isMulti={true}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export function AsyncMultiSelect<Option>({ styles, components, chipVariant, ...props }: AsyncProps<Option, true, GroupBase<Option>> & MultiSelectStyleProps) {
  return (
    <Async
      {...props}
      classNames={MULTI_SELECT_STYLES<Option>(props.error, chipVariant)}
      components={{
        ...MULTI_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      isMulti={true}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export function AsyncCreatableMultiSelect<Option>({
  styles,
  components,
  chipVariant,
  ...props
}: AsyncCreatableProps<Option, true, GroupBase<Option>> & MultiSelectStyleProps) {
  return (
    <AsyncCreatable
      {...props}
      classNames={MULTI_SELECT_STYLES<Option>(props.error, chipVariant)}
      components={{
        ...MULTI_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      isMulti={true}
      unstyled
      styles={{
        control: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.control?.(base, state) }),
        input: (base, inputProps) => ({ ...base, cursor: 'pointer', ...styles?.input?.(base, inputProps) }),
        option: (base, state) => ({ ...base, cursor: 'pointer', ...styles?.option?.(base, state) }),
        ...styles,
      }}
    />
  );
}

export const MULTI_SELECT_COMPONENTS: <Option>() => SelectComponentsConfig<Option, true, GroupBase<Option>> = () => ({
  DropdownIndicator: (props) =>
    props.selectProps.isDisabled ? null : (
      <components.DropdownIndicator {...props}>
        <ArrowDownSmallIcon />
      </components.DropdownIndicator>
    ),
  MultiValueRemove: (props) => (
    <components.MultiValueRemove {...props}>
      <CloseSmallIcon />
    </components.MultiValueRemove>
  ),
  ClearIndicator: (props) => (
    <components.ClearIndicator {...props}>
      <CloseSmallIcon />
    </components.ClearIndicator>
  ),
});

export const MULTI_SELECT_STYLES: <Option>(error?: boolean, chipVariant?: BadgeVariant) => ClassNamesConfig<Option, true> = (error, chipVariant) => {
  // The chip label and remove icon inherit the badge text colour from the chip.
  const chipColourClasses = badgeVariantClasses[chipVariant ?? 'default'];
  return {
    control: (state) =>
      cn(
        'p-[8px] border-grey-3 focus:ring-ring rounded border-2 bg-white px-3 text-sm ring-offset-white focus:outline-none flex flex-wrap items-center',
        state.isFocused && 'border-default-blue',
        error && '!border-default-red',
        state.isDisabled && 'cursor-not-allowed bg-grey-1 text-default-text',
      ),
    menu: () => 'max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-default-text shadow-md mt-1 !pointer-events-auto',
    menuPortal: () => '!z-50 !pointer-events-auto',
    menuList: () => 'p-1',
    option: (state) =>
      cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 !text-sm outline-none',
        state.isFocused && 'bg-grey-1',
        state.isSelected && 'bg-grey-1',
        state.isDisabled && 'cursor-default text-secondary-text pointer-events-none',
      ),
    placeholder: () => 'text-grey-5 leading-none',
    valueContainer: () => 'flex flex-wrap gap-1',
    multiValue: (state) => cn(chipColourClasses, 'rounded-sm font-normal flex items-center', state.isDisabled && 'bg-grey-3 hover:bg-grey-3 text-default-text'),
    multiValueLabel: () => 'pl-[5px] py-0 text-xs',
    multiValueRemove: (state) =>
      cn('hover:bg-transparent px-[2px] rounded-sm focus:ring-2 focus:ring-offset-2 focus:outline-none', state.isDisabled && 'w-0 opacity-0'),
    noOptionsMessage: () => 'text-sm text-grey-5 p-2',
    loadingMessage: () => 'text-sm text-grey-5 p-2',
  };
};
